import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import type { CommentScope, CommentSubjectScope } from "@/domain/comment";
import { getCommentRepository } from "@/infrastructure/di";
import { queryKeys } from "../queryKeys";
import { distinctThreadPages } from "./cache";

export function useCommentThreads(orgId: string, scope: CommentScope, enabled = true) {
  return useInfiniteQuery({
    queryKey: queryKeys.comments.list(orgId, scope),
    initialPageParam: 0,
    queryFn: ({ pageParam, signal }) =>
      getCommentRepository().list({ ...scope, limit: 20, offset: pageParam }, signal),
    getNextPageParam: (page, pages) => {
      const loaded = pages.reduce((count, current) => count + current.threads.length, 0);
      return page.threads.length && loaded < page.total_count ? loaded : undefined;
    },
    select: distinctThreadPages,
    enabled: !!orgId && enabled,
  });
}
export function useCommentThread(orgId: string, uuid: string | null) {
  return useQuery({
    queryKey: queryKeys.comments.detail(orgId, uuid ?? ""),
    queryFn: ({ signal }) => getCommentRepository().get(uuid!, signal),
    enabled: !!orgId && !!uuid,
    staleTime: 0,
  });
}
export function useCommentSummary(orgId: string, scope: CommentSubjectScope, enabled = true) {
  return useQuery({
    queryKey: queryKeys.comments.summary(orgId, scope),
    queryFn: ({ signal }) => getCommentRepository().summary(scope, signal),
    enabled: !!orgId && enabled,
    refetchInterval: 30_000,
  });
}
export function useCommentPeople(orgId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.comments.people(orgId),
    queryFn: ({ signal }) => getCommentRepository().mentionable(signal),
    enabled: !!orgId && enabled,
  });
}
